import { EstateHotelRoomDetails } from "@1games-ru/region-shared/dist/types/estate/estate";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import formatPrice from "@/core/helpers/formatPrice";
import {
  BankCardIcon,
  CashIcon,
  ClockIcon,
  Document4Icon,
  DoorHangerIcon,
  Exit2Icon,
  LockIcon,
  LockOpenIcon,
} from "@/shared/icons";
import { Button, Modal } from "@/shared/ui";
import FeeSelectDaysSlider from "@/shared/widgets/FeeSelectDaysSlider";
import useRoomFee from "@/domains/hotel/hooks/useRoomFee";
import HotelRoomControlView from "@/domains/hotel/components/HotelRoomControlView";
import HotelCheckOutModal from "@/domains/hotel/components/modals/HotelCheckOutModal";
import HotelRegistrationModal from "@/domains/hotel/components/modals/HotelRegistrationModal";

type Props = {
  data: EstateHotelRoomDetails;
  isLoading: boolean;
  isResidentPending: boolean;
  isLockedRoom: boolean;
  setResident: (onConfirm?: () => void) => void;
  toggleLockHotel: () => void;
  onClose: () => void;
  isSelling: boolean;
  sell: () => void;
  refetch?: () => void;
};

export default function HotelRoomControlContainer({
  data,
  isLoading,
  isResidentPending,
  isLockedRoom,
  setResident,
  toggleLockHotel,
  onClose,
  isSelling,
  sell,
  refetch,
}: Props) {
  const { t } = useTranslation("hotel");

  const [selectedDays, setSelectedDays] = useState(1);
  const [isCheckOut, setIsCheckOut] = useState(false);
  const [isRegistration, setIsRegistration] = useState(false);

  const totalPrice = useMemo(() => {
    if (!data?.price || !selectedDays) return 0;
    return data.price * selectedDays;
  }, [data?.price, selectedDays]);

  const { isFeePending, cashPay, cardPay } = useRoomFee(data?.id, totalPrice, () => {
    setSelectedDays(0);
    refetch?.();
  });

  const min = 0;
  const max = data ? Math.ceil((data.maxFeeBalance - data.feeBalance) / data.totalFee) : 0;
  const paidDays = data ? Math.floor(data.feeBalance / data.totalFee) : 0;
  const step = 1;
  const values = useMemo(() => Array.from({ length: max + 1 }, (_, i) => i), [max]);

  const handleDaysChange = (newValue: number) => {
    setSelectedDays(newValue);
  };

  const registrationConfirm = useCallback(() => {
    setResident(() => setIsRegistration(false));
  }, [setResident, setIsRegistration]);

  const title = (
    <div className="flex gap-3 items-center">
      <DoorHangerIcon className="size-6" />
      <p className="text-20 font-bold leading-none">
        {t("hotel.roomControlId", { number: data?.room })}
      </p>
    </div>
  );

  const footer = (
    <div className="flex gap-2">
      <Button
        className="grow gap-2.5 h-11"
        loading={isFeePending}
        disabled={isFeePending || !selectedDays}
        onClick={cashPay}
      >
        <CashIcon className="w-4.5" />
        {t("hotel.paymentMethod.cash")}
      </Button>
      <Button
        className="grow gap-2.5 h-11"
        loading={isFeePending}
        disabled={isFeePending || !selectedDays}
        onClick={cardPay}
      >
        <BankCardIcon className="w-4.5" />
        {t("hotel.paymentMethod.card")}
      </Button>
    </div>
  );

  return (
    <Modal
      open
      title={title}
      footer={footer}
      onClose={onClose}
      className="w-[38.375rem] h-[49rem] pt-6.5 pb-7.5 pl-7.5 pr-6.5"
      classNameCloseBtn="top-6.5 right-7.5"
      style={{ fontFamily: "TT Norms Pro" }}
      disableAnimation
    >
      {isLoading ? (
        <div className="absolute z-10 top-1/2 left-1/2 flex items-center justify-center p-4 -translate-x-1/2 -translate-y-1/2">
          <div className="mz-loader" />
        </div>
      ) : (
        <>
          <HotelRoomControlView
            isLock={isLockedRoom}
            src={data?.images[0]}
            uuid={data?.interiorUuid}
            level={data?.level}
            price={data?.price}
            list={data?.features}
          />
          <div className="flex gap-2">
            <button
              className="flex grow justify-center items-center gap-1.5 h-11 py-3.5 px-5 text-14 font-bold bg-dark-800/50 rounded-xl"
              type="button"
              onClick={() => setIsRegistration(true)}
            >
              <Document4Icon className="size-4 text-white" />
              <p className="text-12 font-bold uppercase leading-none">
                {t("hotel.noRegistration")}
              </p>
            </button>
            <button
              className="flex grow justify-center items-center gap-1.5 h-11 py-3.5 px-5 text-14 font-bold bg-dark-800/50 rounded-xl"
              type="button"
              onClick={() => setIsCheckOut(true)}
            >
              <Exit2Icon className="size-4 text-white" />
              <p className="text-12 font-bold uppercase leading-none">{t("hotel.checkOut")}</p>
            </button>
            <button
              className="flex grow items-center gap-1.5 min-w-[11.5rem] h-11 py-3.5 px-5 text-14 font-bold bg-dark-800/50 rounded-xl"
              type="button"
              onClick={toggleLockHotel}
            >
              {isLockedRoom ? (
                <LockIcon className="size-4 text-white" />
              ) : (
                <LockOpenIcon className="size-4 text-white" />
              )}
              <p className="text-12 font-bold uppercase leading-none">
                {isLockedRoom ? t("hotel.openRoom") : t("hotel.closeRoom")}
              </p>
            </button>
          </div>
          <div className="flex items-center gap-2.5 mb-5 py-5 px-1 border border-[#191919]">
            <ClockIcon className="size-4.5" />
            <p className="text-16 font-medium text-white/70 leading-none">{t("hotel.daysLeft")}:</p>
            <p className="ml-auto text-15 font-bold leading-none">{paidDays}</p>
          </div>
          <p className="flex justify-between mb-6 px-1 text-16 font-bold text-white leading-none">
            {t("hotel.extendRent")}: <span className="text-accent-solid">{selectedDays}</span>
          </p>
          <FeeSelectDaysSlider
            value={selectedDays}
            values={values}
            min={min}
            max={max}
            step={step}
            displayStep={5}
            onChange={handleDaysChange}
            className="px-1.5"
            classNameSlider="[&_.rt-SliderTrack]:h-1 [&_.rt-SliderTrack]:bg-[#191919] [&_.rt-SliderTrack]:rounded-4 [&_.rt-SliderThumb]:w-1.5 [&_.rt-SliderThumb]:h-1.5"
            classNameLabel="mt-1.5"
            highlightActive
          />
          <div className="flex justify-between items-end mt-5 px-1">
            <p className="flex flex-col text-15 font-medium leading-none">
              <span className="mb-0.5 text-20 font-bold text-accent-solid">
                {formatPrice(totalPrice)} ₽
              </span>
              {t("hotel.totalCost")}
            </p>
            <p className="flex flex-col items-end text-15 font-medium text-white/50 leading-none">
              <span className="mb-0.5 text-20 font-bold text-white leading-none">
                {formatPrice(data?.price)} ₽
              </span>
              {t("hotel.costPerDay")}
            </p>
          </div>
          {isCheckOut && (
            <HotelCheckOutModal
              roomNumber={data?.room}
              days={data?.paidDays}
              onClose={() => setIsCheckOut(false)}
              onConfirm={sell}
              isLoading={isSelling}
              isConfirming={isSelling}
            />
          )}
          {isRegistration && (
            <HotelRegistrationModal
              id={data?.room}
              name={data?.hotelName}
              address={`${data.address.streetName} ${data.address.index}`}
              onClose={() => setIsRegistration(false)}
              onConfirm={registrationConfirm}
              isLoading={isResidentPending}
              isConfirming={isResidentPending}
            />
          )}
        </>
      )}
    </Modal>
  );
}
